"use client";

import { useState } from "react";

import {
  PlanInquiryDialog,
  type PlanInquiryDialogLabels,
  type PlanInquiryDialogStatus,
} from "@/components/ui/plan-inquiry-dialog";
import {
  PlanInquiryStatus,
  reducePlanInquiryStatus,
} from "@/lib/plans/plan-inquiry-form-state";
import { submitPlanInquiry } from "@/services/plans/inquiry";
import { Plan } from "@/services/plans/types";
import { TamiButtonPrimary } from "@/components/tami-button";
import { PLAN_INQUIRY_COPY } from "./plan-inquiry.copy";

interface PlanInquiryModalProps {
  plan: Plan;
  ctaLabel: string;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Contato do plano sob consulta. Sem preço publicado o checkout está fechado,
 * então o botão da vitrine abre este formulário em vez de mandar o visitante
 * para `/sign-up` — ele não sai da página nem cai num link externo.
 *
 * A apresentação (casca, cabeçalho, campos, estados) mora no
 * `PlanInquiryDialog` do `@tami/ui`; aqui fica só o estado, a chamada do
 * server action e o botão-gatilho.
 */
export const PlanInquiryModal = ({
  plan,
  ctaLabel,
  className,
  children,
}: PlanInquiryModalProps) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<PlanInquiryStatus>("idle");
  const dispatch = (action: Parameters<typeof reducePlanInquiryStatus>[1]) =>
    setStatus((current) => reducePlanInquiryStatus(current, action));

  // Reabrir depois de enviar precisa mostrar o formulário de novo, não a
  // confirmação da solicitação anterior.
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      dispatch({ type: "reopen" });
    }
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async (data: FormData) => {
    dispatch({ type: "submit" });
    try {
      await submitPlanInquiry(plan.id, {
        name: String(data.get("name") ?? ""),
        email: String(data.get("email") ?? ""),
        whatsapp: String(data.get("whatsapp") ?? ""),
        creatorsCount: data.get("creatorsCount")
          ? Number(data.get("creatorsCount"))
          : null,
        message: String(data.get("message") ?? "") || null,
        website: String(data.get("website") ?? "") || undefined,
      });
      dispatch({ type: "resolved" });
    } catch {
      dispatch({ type: "rejected" });
    }
  };

  // `needs-phone` só é alcançável na porta de dentro do produto (ver
  // `plan-inquiry-form-state.ts`); este modal nunca despacha
  // `phone-required`, mas o tipo do estado é compartilhado com quem
  // despacha — daí o mapeamento defensivo para o status que o diálogo entende.
  const dialogStatus: PlanInquiryDialogStatus =
    status === "needs-phone" ? "error" : status;

  const labels: PlanInquiryDialogLabels = {
    title: dialogStatus === "sent" ? PLAN_INQUIRY_COPY.sentTitle : plan.name,
    description:
      dialogStatus === "sent"
        ? PLAN_INQUIRY_COPY.sentDescription
        : PLAN_INQUIRY_COPY.description,
    closeLabel: PLAN_INQUIRY_COPY.closeLabel,
    nameLabel: PLAN_INQUIRY_COPY.nameLabel,
    emailLabel: PLAN_INQUIRY_COPY.emailLabel,
    whatsappLabel: PLAN_INQUIRY_COPY.whatsappLabel,
    creatorsCountLabel: PLAN_INQUIRY_COPY.creatorsCountLabel,
    messageLabel: PLAN_INQUIRY_COPY.messageLabel,
    sentMessage: PLAN_INQUIRY_COPY.sentMessage,
    errorMessage: PLAN_INQUIRY_COPY.errorMessage,
    submitLabel: PLAN_INQUIRY_COPY.submitLabel,
    submittingLabel: PLAN_INQUIRY_COPY.submittingLabel,
  };

  return (
    <>
      <TamiButtonPrimary className={className} onClick={() => handleOpenChange(true)}>
        {children ?? ctaLabel}
      </TamiButtonPrimary>
      <PlanInquiryDialog
        isOpen={open}
        onClose={handleClose}
        status={dialogStatus}
        labels={labels}
        onSubmit={handleSubmit}
      />
    </>
  );
};
